import moment from "moment";
import { User } from "model/user";
import { BillingCardResource, BillingInvoiceResource, BillingResource } from "api/resource/billing";
import { TagVariant } from "component/shared/tag";
import { BillingInvoiceStatusType } from "type/billing-invoice-status";
import { BillingSubscriptionStateType } from "type/billing-subscription-state";

/** "{package} - ${price} per year|month", as the Account page has always shown the plan. */
export const formatPlanLabel = (user: User): string | undefined => {
	const userPackage = user.userPackage;
	if (!userPackage) {
		return undefined;
	}
	const price = userPackage.packagePrice;
	const term = user.annual ? "per year" : "per month";
	return price
		? `${userPackage.packageName} - $${price} ${term}`
		: userPackage.packageName;
};

/** API dates are yyyy-MM-dd; parsed strictly so a bad value shows "—" rather than "Invalid date". */
export const formatBillingDate = (date?: string | null): string | undefined => {
	if (!date) {
		return undefined;
	}
	const parsed = moment(date, "YYYY-MM-DD", true);
	return parsed.isValid() ? parsed.format("MMMM D, YYYY") : undefined;
};

const titleCase = (value: string) => value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

/** A live subscription (active, in trial, or not started yet) bills on its date; a canceled one shows when it ends. */
export const nextBillingLabel = (summary: Pick<BillingResource, "subscriptionState" | "nextBillingDate">): string => {
	const date = formatBillingDate(summary.nextBillingDate);
	const state = BillingSubscriptionStateType.getByValue(summary.subscriptionState ?? undefined);
	if (!date || !state) {
		return "—";
	}
	if (state.nextBilling === "date") {
		return date;
	}
	return state.nextBilling === "ends" ? `Ends ${date}` : "—";
};

/** The Plan Details Status tag. An unknown state is title-cased rather than hidden. */
export const subscriptionStatusTag = (state?: string | null): { label: string; variant: TagVariant } | undefined => {
	if (!state) {
		return undefined;
	}
	const known = BillingSubscriptionStateType.getByValue(state);
	return known ? { label: known.label, variant: known.tag } : { label: titleCase(state), variant: "neutral" };
};

/** "—" when there is no total, the same as a missing date, rather than "$NaN". */
export const formatMoney = (amount?: number | null, currency?: string | null): string => {
	if (typeof amount !== "number" || isNaN(amount)) {
		return "—";
	}
	try {
		return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(amount);
	} catch{
		return `$${amount.toFixed(2)}`;
	}
};

/**
 * Orders invoices by issue date, treating a missing one as the oldest so it never throws. The
 * Invoices table sorts newest first, so an undated invoice lands at the bottom of that view.
 */
export const compareIssueDates = (a: Pick<BillingInvoiceResource, "issueDate">, b: Pick<BillingInvoiceResource, "issueDate">): number =>
	(a.issueDate ?? "").localeCompare(b.issueDate ?? "");

export const formatCardExpiry = (card: Pick<BillingCardResource, "month" | "year">): string | undefined => {
	if (!card.month || !card.year) {
		return undefined;
	}
	const month = String(card.month).padStart(2, "0");
	const year = String(card.year).slice(-2);
	return `${month}/${year}`;
};

export const invoiceStatusTag = (status?: string | null): { label: string; variant: TagVariant } => {
	const known = BillingInvoiceStatusType.getByValue(status ?? undefined);
	if (known) {
		return { label: known.label, variant: known.tag };
	}
	return { label: titleCase(status ?? "") || "—", variant: "neutral" };
};
